import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { ErrorLike } from '@apollo/client';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Task,
  TaskCompletion,
} from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Typography from '@lib/components/Typography/Typography';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import Table from '@lib/components/Table/Table';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { ContextProps } from 'hooks/useOutletContext';
import TasksReportingFilter from '../TasksReportingFilter/TasksReportingFilter';
import getTaskReportingTableActionsMenu from '../utils/getTaskReportingTableActionsMenu';
import getTaskReportingTableActions from '../utils/getTaskReportingTableActions';
import useTaskReportingActions from '../utils/useTaskReportingActions';
import TaskCompletionDetails from '../TaskCompletionDetails';
import styles from '../TaskReportingView.module.scss';

type Props = {
  task?: Task;
  taskError?: ErrorLike;
  taskId?: string;
  taskLoading?: boolean;
};

export default function TaskReportingView(props: Props) {
  const { task, taskError, taskId, taskLoading } = props;
  const { t } = useTranslation();

  const { getUserAccessByCompany }: ContextProps = useOutletContext();

  const {
    filterCounter,
    filterDefaultValues,
    filterFields,
    minDate,
    maxDate,
    periodValue,
    onPeriodChange,
    onResetFilters,
    onSubmitFilter,
    tableColumns,
    tableData,
    tableLoading,
    tableFirstLoading,
    tableError,
    onViewTaskCompletionHandler,
    onCloseTaskCompletionHandler,
    onCreateIntervention,
    onChangeSelectedTaskCompletionHandler,
    onViewIntervention,
    selectedCompletionId,
    fetchIds,
  } = useTaskReportingActions({ task, taskId });

  const isInterventionsLocked =
    getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.LimitedRead;
  const actions = isInterventionsLocked
    ? getTaskReportingTableActions({ onViewTaskCompletionHandler })
    : undefined;
  const actionsMenu = isInterventionsLocked
    ? undefined
    : getTaskReportingTableActionsMenu({
        t,
        onViewTaskCompletionHandler,
        onCreateIntervention,
        onViewIntervention,
      });

  if (!taskId) {
    return <ErrorPage status={404} />;
  }

  if (taskError)
    return (
      <ErrorPage
        status={getStatusFromError(taskError)}
        description={getDescriptionFromError(taskError)}
      />
    );

  const emptyState = filterCounter ? (
    <ListEmptyState
      title={t('no-results-found')}
      text={t('try-something-else')}
    />
  ) : (
    <ListEmptyState
      title={t('no-task-reporting-yet')}
      text={t('no-task-reporting-text')}
    />
  );

  return (
    <>
      {taskLoading ? (
        <CircleLoader />
      ) : (
        <div className={styles.root}>
          <WhiteBox className={styles.headerCard}>
            <Typography variant="h2">{task?.name}</Typography>
          </WhiteBox>
          <TasksReportingFilter
            counter={filterCounter}
            defaultValues={filterDefaultValues}
            filterFields={filterFields}
            minDate={minDate}
            maxDate={maxDate}
            onPeriodChange={onPeriodChange}
            onResetFilters={onResetFilters}
            onSubmitFilter={onSubmitFilter}
            periodValue={periodValue}
          />
          <WhiteBox className={styles.tableCard} horizontalPaddings="custom">
            {tableError ? (
              <ErrorPage
                status={getStatusFromError(tableError)}
                description={getDescriptionFromError(tableError)}
              />
            ) : (
              <div className={styles.tableWrapper}>
                {tableFirstLoading ? (
                  <CircleLoader />
                ) : (
                  <Table<TaskCompletion>
                    actions={actions}
                    actionsMenu={actionsMenu}
                    columns={tableColumns}
                    data={tableData}
                    emptyState={emptyState}
                    id="task-reporting-completions"
                    loading={tableLoading}
                    totalEntries={tableData.length}
                  />
                )}
              </div>
            )}
          </WhiteBox>
        </div>
      )}
      <TaskCompletionDetails
        completionId={selectedCompletionId}
        onClose={onCloseTaskCompletionHandler}
        onChangeCompletionId={onChangeSelectedTaskCompletionHandler}
        fetchIds={fetchIds}
        isFilterApplied={!!filterCounter}
      />
    </>
  );
}
